import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { getAuth, signInWithEmailAndPassword, createUserWithEmailAndPassword, signOut, onAuthStateChanged } from 'firebase/auth';
import { setUserOnline, setUserOffline } from '../utils/onlineStatus';
import { apiPost, apiGet } from '../utils/api';

const useAuthStore = create(
  persist(
    (set, get) => ({
      user: null,
      loading: false,
      error: null,
      isAdmin: false,
      hydrated: false,
      authInitialized: false, // New flag to track Firebase auth initialization

      // Initialize authentication state
      initializeAuth: async () => {
        set({ loading: true, error: null });
        
        return new Promise((resolve) => {
          const auth = getAuth();
          
          // Listen for Firebase auth state changes
          const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
            try {
              if (firebaseUser) {
                // User is signed in with Firebase, now check backend session
                const res = await apiGet('/api/auth/me');
                const data = await res.json();
                
                if (res.ok && data && data.email) {
                  const user = data;
                  const isAdmin = user.role === 'admin';
                  set({ 
                    user, 
                    isAdmin, 
                    loading: false, 
                    hydrated: true,
                    authInitialized: true,
                    error: null 
                  });
                  setUserOnline(firebaseUser);
                } else {
                  // Firebase user exists but no valid backend session
                  set({ 
                    user: null, 
                    isAdmin: false, 
                    loading: false, 
                    hydrated: true,
                    authInitialized: true 
                  });
                  setUserOffline(firebaseUser);
                }
              } else {
                // No Firebase user
                set({ 
                  user: null, 
                  isAdmin: false, 
                  loading: false, 
                  hydrated: true,
                  authInitialized: true 
                });
              }
            } catch (error) {
              // Handle network errors gracefully
              set({ 
                user: null, 
                isAdmin: false, 
                loading: false, 
                hydrated: true,
                authInitialized: true 
              });
            }
            resolve();
          });
          
          // Cleanup function
          return () => unsubscribe();
        });
      },

      login: async (email, password) => {
        set({ loading: true, error: null });
        try {
          const auth = getAuth();
          const userCredential = await signInWithEmailAndPassword(auth, email, password);
          
          // Get backend user data
          const backendRes = await apiPost('/api/auth/login', { email, password });
          
          const backendData = await backendRes.json();
          if (!backendRes.ok) {
            throw new Error(backendData.message || 'Login failed');
          }

          // Set user with backend data
          const user = backendData.user;
          const isAdmin = user.role === 'admin';
          set({ 
            user, 
            loading: false, 
            isAdmin,
            error: null 
          });
          
          setUserOnline(userCredential.user);
          return user;
        } catch (error) {
          set({ error: 'Login failed. Please check your credentials.', loading: false });
          throw error;
        }
      },

      signup: async (email, password) => {
        set({ loading: true, error: null });
        try {
          const auth = getAuth();
          const userCredential = await createUserWithEmailAndPassword(auth, email, password);
          
          // Get backend user data
          const backendRes = await apiPost('/api/auth/register', { email, password });
          
          const backendData = await backendRes.json();
          if (!backendRes.ok) {
            throw new Error(backendData.message || 'Registration failed');
          }

          // Set user with backend data
          const user = backendData.user;
          const isAdmin = user.role === 'admin';
          set({ 
            user, 
            loading: false, 
            isAdmin,
            error: null 
          });
          
          setUserOnline(userCredential.user);
          return user;
        } catch (error) {
          set({ error: 'Registration failed. Please try again.', loading: false });
          throw error;
        }
      },

      logout: async () => {
        set({ user: null, loading: false, isAdmin: false });
        try {
          await apiPost('/api/auth/logout');
          const auth = getAuth();
          const user = auth.currentUser;
          setUserOffline(user);
          await signOut(auth);
        } catch (error) {
          // Silent error handling for logout
        }
      },

      setUser: (user) => {
        if (user) {
          if (!user.uid && user.id) {
            user.uid = user.id;
          }
          const isAdmin = user.role === 'admin';
          set({ user, isAdmin, hydrated: true });
        } else {
          set({ user: null, isAdmin: false, hydrated: true });
        }
      },
      
      checkSession: async () => {
        // Only check session if auth is not initialized yet
        if (get().authInitialized) {
          return;
        }
        
        set({ loading: true });
        try {
          const res = await apiGet('/api/auth/me');
          const data = await res.json();
          if (res.ok && data && data.email) {
            const user = data;
            const isAdmin = user.role === 'admin';
            set({ user, isAdmin, loading: false });
          } else {
            set({ user: null, isAdmin: false, loading: false });
          }
        } catch (error) {
          set({ user: null, isAdmin: false, loading: false });
        }
        set({ hydrated: true });
      },

      // Getter for checking if auth is still loading
      getAuthLoading: () => {
        const state = get();
        return !state.hydrated || !state.authInitialized || state.loading;
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({ 
        user: state.user,
        isAdmin: state.isAdmin
      }),
    }
  )
);

export default useAuthStore; 